import type { Competitor } from '../types';

// Researched 2026-11-03. Every claim links to the page it came from.
// To correct something: change it here, update sourceUrl and lastReviewed.

export const beprofit: Competitor = {
  "id": "beprofit",
  "name": "BeProfit",
  "group": "ecomm",
  "website": "https://beprofit.ai",
  "owner": "Viably (acquired October 2024, per CB Insights); still sold under the BeProfit name",
  "summary": "A profit analytics app, mainly for Shopify stores, that combines order revenue, product costs, shipping, fees and ad spend into a profit and P&L dashboard. It is built for small and mid-size DTC merchants who want profit numbers without a data team.",
  "bestFor": [
    "Single-store Shopify merchants who want order-level and SKU-level contribution profit within a day of installing",
    "Small teams that mostly sell on Shopify, sometimes with WooCommerce or Amazon, and advertise mainly on Meta and Google",
    "Owners who want to ask profit questions in Claude or ChatGPT (read-only MCP access is included on every plan)"
  ],
  "notFor": [
    "Brands whose TikTok Shop sales do not flow through Shopify (TikTok is supported only as a Shopify sales channel)",
    "Multi-channel brands with wholesale, retail POS or ERP data to combine; no such connectors are documented",
    "Teams that want to define their own metrics: custom metrics are Plus-only and built by BeProfit staff on request",
    "Anyone who needs raw, modeled data in their own warehouse for SQL or BI"
  ],
  "strengths": [
    "Much faster and cheaper to start than a custom warehouse build: there is a free plan up to 150 orders/month, and paid plans start at $49/month",
    "Contribution profit per order (revenue minus COGS, ad spend, shipping and fees) comes ready-made, so no modeling work is needed",
    "Ready-made profit tools, including break-even and price-to-margin simulators, per-SKU margin, inventory days-remaining and net shipping recovery by carrier",
    "Ad spend from Meta and Google is pulled in automatically and shown as profit on ad spend next to platform ROAS",
    "A read-only MCP connection to Claude and ChatGPT is included on all plans, metered by monthly credits",
    "There is no warehouse, transformation code or BI license to maintain"
  ],
  "limitations": [
    "Custom metrics are available only on Plus. They are not self-serve: you request them from support, they go through a technical review, they must use data BeProfit already tracks, and they take about 3-5 business days",
    "TikTok Shop sales are visible only when the TikTok shop is connected as a Shopify sales channel; no direct TikTok Shop integration is described",
    "No ERP, wholesale (for example Faire) or retail POS connectors are listed; documented integrations cover Shopify, Amazon and WooCommerce plus Meta, Google, Klaviyo, ShipStation and UPS",
    "Data leaves the app as CSV export, or through an API on Plus. No sync to Snowflake or BigQuery is documented",
    "Data history depends on the plan: 1 year on Basic and Pro, 2 years on Ultimate, and all-time only on Plus",
    "Basic, Pro and Ultimate cover one store only, so multi-store reporting needs Plus or Enterprise",
    "Refund handling is not documented on the platform page",
    "On a Capterra listing with only 4 reviews, reviewers mention time-consuming initial cost data entry and slow speed (third-party, small sample)"
  ],
  "pricing": {
    "model": "Tiered subscription, priced by monthly order volume",
    "publicStartingPrice": "Free up to 150 orders/month; paid plans from $49/month (Basic, 450 orders)",
    "freeTier": "yes - Free plan up to 150 orders/month with a basic profit dashboard and 6 months of history in MCP. The Shopify listing also offers a 14-day trial on paid plans.",
    "notes": "beprofit.ai/pricing (checked 2026-11-03): Basic $49/mo (450 orders), Pro $99 (900), Ultimate $149 (1,700; adds UTM attribution, LTV cohorts and simulators), Plus $249 (custom metrics, API, all-time history). An Enterprise band runs from $249 to $1,499/mo depending on order volume (up to 3,000 orders at $249; 75,000+ at $1,499) and adds multi-store. On monthly plans, extra orders cost $0.30 each, capped at $500 per cycle; annual plans have no overage. The Shopify App Store listing shows the same four paid tiers with annual prices about 20% lower (for example Basic $468/yr) and describes Plus as unlimited orders and shops. The vendor page is internally inconsistent: it mentions a 'Scale' plan in places, and Plus and Enterprise share the $249 price. Cost is driven mainly by order volume, then by the number of stores and history depth. The brand's site moved from beprofit.co to beprofit.ai (the old URL redirects). The vendor does not publish a setup time; reviewers on a small Capterra sample call initial cost entry time-consuming.",
    "sourceUrl": "https://beprofit.ai/pricing/",
    "checkedOn": "2026-11-03"
  },
  "features": {
    "shopify": {
      "value": "yes",
      "note": "Core platform; Shopify App Store listing since Sept 2020.",
      "sourceUrl": "https://apps.shopify.com/beprofit-profit-tracker"
    },
    "amazon": {
      "value": "yes",
      "note": "Amazon is listed as a supported sales channel alongside Shopify and WooCommerce. Depth of fee and settlement coverage was not verified.",
      "sourceUrl": "https://beprofit.ai/platform/"
    },
    "tiktok_shop": {
      "value": "partial",
      "note": "TikTok sales are supported only as a Shopify sales channel, not through a direct TikTok Shop connection.",
      "sourceUrl": "https://help.runviably.com/beprofit/existing-tiktok-related-functionality-in-beprofit",
      "badge": "Via Shopify only"
    },
    "wholesale_retail_or_erp": {
      "value": "unknown",
      "note": "No ERP, wholesale or POS connectors are listed. Shipping integrations (ShipStation, UPS) are the only operational sources.",
      "sourceUrl": "https://beprofit.ai/platform/"
    },
    "ad_platforms": {
      "value": "partial",
      "note": "Meta Ads and Google Ads are confirmed on vendor pages. TikTok Ads is not confirmed on the vendor site.",
      "sourceUrl": "https://beprofit.ai/platform/"
    },
    "first_party_pixel_attribution": {
      "value": "partial",
      "note": "UTM-based attribution on Ultimate and above. No proprietary pixel or multi-touch model is described.",
      "sourceUrl": "https://apps.shopify.com/beprofit-profit-tracker"
    },
    "mmm_incrementality": {
      "value": "unknown",
      "note": "Not mentioned on vendor pages.",
      "sourceUrl": "https://beprofit.ai/platform/"
    },
    "profit_cogs": {
      "value": "yes",
      "note": "Per-order contribution profit net of COGS, ad spend, shipping and fees; per-SKU COGS and margin.",
      "sourceUrl": "https://beprofit.ai/platform/"
    },
    "ltv_cohorts": {
      "value": "yes",
      "note": "LTV cohort analysis on Ultimate ($149/mo) and above.",
      "sourceUrl": "https://beprofit.ai/pricing/"
    },
    "subscription_analytics": {
      "value": "unknown",
      "note": "No Recharge or subscription integration is listed.",
      "sourceUrl": "https://beprofit.ai/platform/"
    },
    "warehouse_access": {
      "value": "unknown",
      "note": "CSV export on all plans and an API on Plus. No warehouse sync is documented.",
      "sourceUrl": "https://beprofit.ai/pricing/"
    },
    "custom_metrics": {
      "value": "partial",
      "note": "Plus plan only. Requested from support and built by BeProfit after a feasibility review; not self-serve.",
      "sourceUrl": "https://help.runviably.com/beprofit/custom-metrics-creation-for-plus-plan-members",
      "badge": "Plus plan, by request"
    },
    "custom_sql": {
      "value": "unknown",
      "note": "No SQL interface is mentioned.",
      "sourceUrl": "https://beprofit.ai/platform/"
    },
    "ai_assistant": {
      "value": "yes",
      "note": "Read-only MCP connection to Claude and ChatGPT on all plans, metered by monthly credits.",
      "sourceUrl": "https://beprofit.ai/pricing/"
    },
    "mobile_app": {
      "value": "unknown",
      "note": "Capterra lists iOS and Android, but vendor pages do not mention a mobile app, so this is unconfirmed.",
      "sourceUrl": "https://www.capterra.com/p/214079/BeProfit/"
    }
  },
  "recentChanges": [
    {
      "date": "2024-10",
      "change": "BeProfit was acquired by Viably, an ecommerce funding marketplace. Some directories now list the product as 'Viably Profit Analytics (formerly BeProfit)', and help docs are hosted on help.runviably.com.",
      "sourceUrl": "https://www.cbinsights.com/investor/beprofit"
    },
    {
      "date": "2026",
      "change": "The site moved from beprofit.co to beprofit.ai. A Free plan (150 orders) and an order-banded Enterprise tier ($249-$1,499/mo) appear on the pricing page.",
      "sourceUrl": "https://beprofit.ai/pricing/"
    },
    {
      "date": "2026",
      "change": "Added a read-only MCP connection so merchants can query profit data from Claude or ChatGPT, metered with credits. The exact launch date was not found.",
      "sourceUrl": "https://beprofit.ai/platform/"
    }
  ],
  "sources": [
    {
      "title": "BeProfit pricing",
      "url": "https://beprofit.ai/pricing/"
    },
    {
      "title": "BeProfit platform overview",
      "url": "https://beprofit.ai/platform/"
    },
    {
      "title": "BeProfit on the Shopify App Store",
      "url": "https://apps.shopify.com/beprofit-profit-tracker"
    },
    {
      "title": "BeProfit help: TikTok functionality",
      "url": "https://help.runviably.com/beprofit/existing-tiktok-related-functionality-in-beprofit"
    },
    {
      "title": "BeProfit help: custom metrics for Plus",
      "url": "https://help.runviably.com/beprofit/custom-metrics-creation-for-plus-plan-members"
    },
    {
      "title": "CB Insights: BeProfit (acquisition by Viably)",
      "url": "https://www.cbinsights.com/investor/beprofit"
    },
    {
      "title": "Ecommercetech.io: Viably Profit Analytics (formerly BeProfit)",
      "url": "https://ecommercetech.io/apps/viably-profit-analytics"
    },
    {
      "title": "Capterra: BeProfit (third-party)",
      "url": "https://www.capterra.com/p/214079/BeProfit/"
    }
  ],
  "lastReviewed": "2026-11-03"
};
